import React from 'react';
import L from 'leaflet';
import {
  CircleMarker,
  MapContainer,
  Polyline,
  Popup,
  TileLayer,
  Tooltip,
  ZoomControl,
  useMap
} from 'react-leaflet';
import {
  AlertCircle,
  ArrowRight,
  Crosshair,
  Eye,
  EyeOff,
  Layers,
  Maximize2,
  Search,
  Waypoints,
  X
} from 'lucide-react';
import { cn } from '../../lib/cn';
import {
  DEFAULT_CENTER,
  formatDistance,
  getRouteDisplayName,
  getSortedRouteStops,
  highlightMatch,
  routeSegmentDistances
} from '../../utils/routeManagementHelpers';

const GLASS = 'bg-white/95 backdrop-blur-md border border-slate-200 shadow-pop rounded-xl';

const DIRECTION_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'up', label: 'Up' },
  { key: 'down', label: 'Down' }
];

/*
 * Direction as stored varies between databases ("Up", "UP", "1", "Down", "0"…),
 * so it is normalised here rather than compared as a raw string. Anything
 * unrecognised is kept as-is and still shown, never dropped.
 */
function normalizeDirection(direction) {
  const raw = String(direction ?? '').trim();
  const text = raw.toLowerCase();
  if (['up', 'u', '1'].includes(text)) return { key: 'up', label: 'Up' };
  if (['down', 'dn', 'd', '0'].includes(text)) return { key: 'down', label: 'Down' };
  return { key: 'other', label: raw || '—' };
}

const DIRECTION_ORDER = { up: 0, down: 1, other: 2 };

/*
 * Only stops with real coordinates are drawn. A route stop whose master no longer
 * resolves can arrive with missing, non-numeric or 0,0 coordinates, and drawing
 * it would throw a line off into the ocean and wreck the zoom-to-fit.
 */
function toLocatedStop(stop) {
  const lat = Number(stop?.lat);
  const lng = Number(stop?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { ...stop, lat, lng };
}

// Evenly spread, well-separated hues (golden angle), one per route number.
function networkColor(index) {
  const hue = (index * 137.508 + 220) % 360;
  return `hsl(${hue.toFixed(1)}, 78%, 40%)`;
}

function compareRouteNumbers(left, right) {
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
}

// Fits the map to `bounds` whenever `fitKey` changes, and only then — so panning
// around is never undone by an unrelated re-render.
function FitBounds({ bounds, fitKey }) {
  const map = useMap();
  React.useEffect(() => {
    if (!bounds || !bounds.isValid()) return;
    map.fitBounds(bounds, { padding: [48, 48], maxZoom: 16, animate: true });
  }, [fitKey, map]);
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  React.useEffect(() => {
    if (!target) return;
    map.flyTo([target.lat, target.lng], Math.max(map.getZoom(), 16), { duration: 0.6 });
  }, [target, map]);
  return null;
}

// Leaflet only notices window resizes; collapsing the sidebar changes the map's
// width without one, which would leave a grey strip. Watch the container instead.
function KeepSized() {
  const map = useMap();
  React.useEffect(() => {
    const container = map.getContainer();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(container);
    return () => observer.disconnect();
  }, [map]);
  return null;
}

function boundsFor(routes) {
  const points = routes.flatMap((route) => route.positions);
  return points.length ? L.latLngBounds(points) : null;
}

export default function NetworkMapPage({ controller }) {
  const { savedRoutes, stops: masterStops, resolveStopMasterName } = controller;

  // The resolver is a fresh function on every render; what it actually depends on
  // is the stop master list, so that is what the route list is keyed on.
  const resolveRef = React.useRef(resolveStopMasterName);
  resolveRef.current = resolveStopMasterName;

  const [search, setSearch] = React.useState('');
  const [directionFilter, setDirectionFilter] = React.useState('all');
  const [hiddenIds, setHiddenIds] = React.useState(() => new Set());
  const [selectedId, setSelectedId] = React.useState(null);
  const [hoveredId, setHoveredId] = React.useState(null);
  const [isolate, setIsolate] = React.useState(false);
  const [mutedBasemap, setMutedBasemap] = React.useState(true);
  const [fitKey, setFitKey] = React.useState(0);
  const [flyTarget, setFlyTarget] = React.useState(null);
  const [focusedStopKey, setFocusedStopKey] = React.useState(null);
  const rowRefs = React.useRef(new Map());

  // Every route, shaped once for drawing and listing.
  const networkRoutes = React.useMemo(() => {
    const numbers = [
      ...new Set((savedRoutes || []).map((route) => String(route.routeNumber ?? '').trim()))
    ].sort(compareRouteNumbers);
    const colorByNumber = new Map(numbers.map((number, index) => [number, networkColor(index)]));

    return (savedRoutes || [])
      .map((route) => {
        const number = String(route.routeNumber ?? '').trim();
        const allStops = getSortedRouteStops(route);
        const stops = allStops.map(toLocatedStop).filter(Boolean);
        const resolveName = (stop) => resolveRef.current?.(stop) || stop?.stopName || '';
        return {
          id: route._id,
          number: number || '—',
          routeId: route.routeId || '',
          direction: normalizeDirection(route.direction),
          label: getRouteDisplayName(route, resolveName) || route.routeName || 'Unnamed route',
          color: colorByNumber.get(number),
          stops,
          positions: stops.map((stop) => [stop.lat, stop.lng]),
          lengthMeters: routeSegmentDistances(stops).total,
          totalStops: allStops.length,
          unlocated: allStops.length - stops.length,
          stopNames: allStops.map((stop) => String(resolveName(stop) || '').toLowerCase())
        };
      })
      .sort(
        (left, right) =>
          compareRouteNumbers(left.number, right.number) ||
          DIRECTION_ORDER[left.direction.key] - DIRECTION_ORDER[right.direction.key] ||
          left.label.localeCompare(right.label)
      );
  }, [savedRoutes, masterStops]);

  // Routes that pass the search and direction filter: shown in the list and on the map.
  const filteredRoutes = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return networkRoutes.filter((route) => {
      if (directionFilter !== 'all' && route.direction.key !== directionFilter) return false;
      if (!query) return true;
      return (
        route.number.toLowerCase().includes(query) ||
        route.routeId.toLowerCase().includes(query) ||
        route.label.toLowerCase().includes(query) ||
        route.stopNames.some((name) => name.includes(query))
      );
    });
  }, [networkRoutes, search, directionFilter]);

  const selectedRoute = React.useMemo(
    () => networkRoutes.find((route) => route.id === selectedId) || null,
    [networkRoutes, selectedId]
  );

  // A selection that disappears (route deleted, data reloaded) is dropped.
  React.useEffect(() => {
    if (selectedId && !selectedRoute) setSelectedId(null);
  }, [selectedId, selectedRoute]);

  const drawnRoutes = React.useMemo(() => {
    if (selectedRoute && isolate) return [];
    return filteredRoutes.filter(
      (route) => !hiddenIds.has(route.id) && route.id !== selectedId && route.positions.length > 1
    );
  }, [filteredRoutes, hiddenIds, selectedId, selectedRoute, isolate]);

  const hoveredRoute =
    hoveredId && hoveredId !== selectedId
      ? filteredRoutes.find((route) => route.id === hoveredId && route.positions.length > 1)
      : null;

  const groups = React.useMemo(() => {
    const byNumber = new Map();
    filteredRoutes.forEach((route) => {
      if (!byNumber.has(route.number)) byNumber.set(route.number, []);
      byNumber.get(route.number).push(route);
    });
    return [...byNumber.entries()];
  }, [filteredRoutes]);

  // Zoom target: the selected route, otherwise everything currently drawn.
  const fitBounds = React.useMemo(() => {
    if (selectedRoute && selectedRoute.positions.length) return boundsFor([selectedRoute]);
    const visible = filteredRoutes.filter((route) => !hiddenIds.has(route.id));
    return boundsFor(visible.length ? visible : networkRoutes);
  }, [selectedRoute, filteredRoutes, hiddenIds, networkRoutes]);

  // Fit once when the routes first arrive, and again when the direction filter changes.
  const hasRoutes = networkRoutes.length > 0;
  React.useEffect(() => {
    if (hasRoutes) setFitKey((key) => key + 1);
  }, [hasRoutes, directionFilter]);

  function selectRoute(id, { scroll = false } = {}) {
    setSelectedId((current) => (current === id ? null : id));
    setFocusedStopKey(null);
    setFitKey((key) => key + 1);
    if (scroll) {
      requestAnimationFrame(() => {
        rowRefs.current.get(id)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    }
  }

  function clearSelection() {
    setSelectedId(null);
    setFocusedStopKey(null);
    setIsolate(false);
    setFitKey((key) => key + 1);
  }

  function toggleHidden(id) {
    setHiddenIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function setAllHidden(hidden) {
    setHiddenIds((current) => {
      const next = new Set(current);
      filteredRoutes.forEach((route) => (hidden ? next.add(route.id) : next.delete(route.id)));
      return next;
    });
  }

  React.useEffect(() => {
    function onKey(event) {
      if (event.key === 'Escape' && selectedId) clearSelection();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const shownCount = filteredRoutes.filter((route) => !hiddenIds.has(route.id)).length;
  const hiddenInFilter = filteredRoutes.length - shownCount;
  const dimOthers = !!selectedRoute;

  return (
    <div className="flex-1 min-h-0 flex overflow-hidden">
      {/* Map */}
      <div className="relative flex-1 min-w-0">
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={12}
          zoomControl={false}
          preferCanvas
          style={{ height: '100%', width: '100%' }}
        >
          <ZoomControl position="bottomright" />
          <TileLayer
            key={mutedBasemap ? 'muted' : 'standard'}
            className={mutedBasemap ? 'network-tiles-muted' : ''}
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          />
          <KeepSized />
          <FitBounds bounds={fitBounds} fitKey={fitKey} />
          <FlyTo target={flyTarget} />

          {drawnRoutes.map((route) => (
            <Polyline
              key={route.id}
              positions={route.positions}
              pathOptions={{
                color: route.color,
                weight: dimOthers ? 3 : 4,
                opacity: dimOthers ? 0.22 : 0.85,
                lineCap: 'round',
                lineJoin: 'round'
              }}
              eventHandlers={{
                click: () => selectRoute(route.id, { scroll: true }),
                mouseover: () => setHoveredId(route.id),
                mouseout: () => setHoveredId((current) => (current === route.id ? null : current))
              }}
            >
              <Tooltip sticky direction="top" opacity={1}>
                <RouteTooltip route={route} />
              </Tooltip>
            </Polyline>
          ))}

          {hoveredRoute && !(selectedRoute && isolate) && (
            <React.Fragment key={`hover-${hoveredRoute.id}`}>
              <Polyline
                positions={hoveredRoute.positions}
                pathOptions={{ color: '#ffffff', weight: 9, opacity: 0.9, interactive: false }}
              />
              <Polyline
                positions={hoveredRoute.positions}
                pathOptions={{ color: hoveredRoute.color, weight: 5, opacity: 1, interactive: false }}
              />
            </React.Fragment>
          )}

          {selectedRoute && selectedRoute.positions.length > 0 && (
            <React.Fragment key={`selected-${selectedRoute.id}`}>
              <Polyline
                positions={selectedRoute.positions}
                pathOptions={{ color: '#ffffff', weight: 11, opacity: 0.95, interactive: false }}
              />
              <Polyline
                positions={selectedRoute.positions}
                pathOptions={{
                  color: selectedRoute.color,
                  weight: 6,
                  opacity: 1,
                  lineCap: 'round',
                  lineJoin: 'round',
                  interactive: false
                }}
              />
              {selectedRoute.stops.map((stop, index) => {
                const isEnd = index === 0 || index === selectedRoute.stops.length - 1;
                const stopKey = `${stop.sequence}-${index}`;
                const focused = focusedStopKey === stopKey;
                return (
                  <CircleMarker
                    key={stopKey}
                    center={[stop.lat, stop.lng]}
                    radius={focused ? 9 : isEnd ? 7 : 4.5}
                    pathOptions={{
                      color: isEnd ? '#ffffff' : selectedRoute.color,
                      weight: isEnd ? 3 : 2,
                      fillColor: index === 0 ? '#059669' : isEnd ? '#dc2626' : '#ffffff',
                      fillOpacity: 1
                    }}
                  >
                    <Tooltip direction="top" offset={[0, -6]} opacity={1}>
                      <span className="text-[12px]">
                        <strong>#{stop.sequence ?? index + 1}</strong> {stop.stopName}
                      </span>
                    </Tooltip>
                    <Popup>
                      <strong>
                        #{stop.sequence ?? index + 1} {stop.stopName}
                      </strong>
                      {stop.routeStopCode && (
                        <>
                          <br />
                          <span className="font-mono text-[11px] text-slate-500">{stop.routeStopCode}</span>
                        </>
                      )}
                      <br />
                      {stop.lat.toFixed(6)}, {stop.lng.toFixed(6)}
                    </Popup>
                  </CircleMarker>
                );
              })}
            </React.Fragment>
          )}
        </MapContainer>

        {/* Overview card */}
        <div className={cn(GLASS, 'absolute top-3 left-3 z-[1000] px-3.5 py-2.5 max-w-[calc(100%-7rem)]')}>
          <div className="flex items-center gap-1.5 text-slate-500">
            <Waypoints className="h-3.5 w-3.5" />
            <span className="text-[10.5px] font-semibold uppercase tracking-wider">Route network</span>
          </div>
          {selectedRoute ? (
            <p className="mt-1 text-[13px] font-semibold text-slate-900 truncate">
              <span
                className="inline-block h-2.5 w-2.5 rounded-full mr-1.5 align-middle"
                style={{ backgroundColor: selectedRoute.color }}
              />
              Route {selectedRoute.number} · {selectedRoute.direction.label}
            </p>
          ) : (
            <p className="mt-1 text-[13px] text-slate-700">
              <span className="font-semibold text-slate-900 tabular-nums">{shownCount}</span> of{' '}
              <span className="tabular-nums">{networkRoutes.length}</span> routes on map
            </p>
          )}
        </div>

        {/* Map tools */}
        <div className="absolute top-3 right-3 z-[1000] flex flex-col gap-1.5">
          <MapToolButton
            label="Fit all routes"
            icon={Maximize2}
            onClick={() => {
              if (selectedRoute) clearSelection();
              else setFitKey((key) => key + 1);
            }}
          />
          <MapToolButton
            label={mutedBasemap ? 'Standard basemap' : 'Muted basemap'}
            icon={Layers}
            active={!mutedBasemap}
            onClick={() => setMutedBasemap((value) => !value)}
          />
        </div>

        {networkRoutes.length === 0 && (
          <div className="absolute inset-0 z-[1000] flex items-center justify-center pointer-events-none">
            <div className={cn(GLASS, 'px-4 py-3 flex items-center gap-2.5 pointer-events-auto')}>
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <p className="text-[13px] text-slate-700">No routes to show yet.</p>
            </div>
          </div>
        )}
      </div>

      {/* Route list */}
      <aside className="w-[300px] xl:w-[340px] shrink-0 border-l border-slate-200 bg-white flex flex-col min-h-0">
        <div className="p-3 border-b border-slate-100 space-y-2.5">
          <div className="flex items-center rounded-lg border border-slate-200 bg-white pl-2.5 pr-1 focus-within:border-brand-300 focus-within:ring-2 focus-within:ring-brand-100">
            <Search className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Route no., name or stop"
              className="flex-1 min-w-0 bg-transparent border-0 outline-none px-2 py-1.5 text-[13px] placeholder:text-slate-400"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="h-6 w-6 inline-flex items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Clear search"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="flex rounded-md border border-slate-200 p-0.5 bg-slate-50">
              {DIRECTION_FILTERS.map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => setDirectionFilter(filter.key)}
                  className={cn(
                    'h-6 px-2.5 rounded text-[12px] font-medium transition-colors',
                    directionFilter === filter.key
                      ? 'bg-white text-brand-700 shadow-soft'
                      : 'text-slate-500 hover:text-slate-800'
                  )}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <div className="ml-auto flex items-center gap-1">
              <button
                type="button"
                onClick={() => setAllHidden(false)}
                disabled={hiddenInFilter === 0}
                className="h-6 px-1.5 rounded text-[11.5px] font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                Show all
              </button>
              <button
                type="button"
                onClick={() => setAllHidden(true)}
                disabled={shownCount === 0}
                className="h-6 px-1.5 rounded text-[11.5px] font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                Hide all
              </button>
            </div>
          </div>
        </div>

        {selectedRoute && (
          <SelectedRouteCard
            route={selectedRoute}
            isolate={isolate}
            focusedStopKey={focusedStopKey}
            onToggleIsolate={() => setIsolate((value) => !value)}
            onZoom={() => setFitKey((key) => key + 1)}
            onClose={clearSelection}
            onStopClick={(stop, stopKey) => {
              setFocusedStopKey(stopKey);
              setFlyTarget({ lat: stop.lat, lng: stop.lng, at: Date.now() });
            }}
          />
        )}

        <div className="flex-1 min-h-0 overflow-y-auto">
          {groups.length === 0 && (
            <p className="px-4 py-8 text-center text-[12.5px] text-slate-500">
              {networkRoutes.length === 0 ? 'No routes found.' : 'No routes match this search.'}
            </p>
          )}

          {groups.map(([number, routes]) => (
            <div key={number} className="border-b border-slate-100 last:border-b-0">
              <div className="sticky top-0 z-[1] flex items-center gap-2 px-3 pt-2.5 pb-1.5 bg-white/95 backdrop-blur">
                <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: routes[0].color }} />
                <span className="text-[12.5px] font-semibold text-slate-900">Route {highlightMatch(number, search)}</span>
                <span className="ml-auto text-[11px] text-slate-400 tabular-nums">
                  {routes.length} direction{routes.length === 1 ? '' : 's'}
                </span>
              </div>

              {routes.map((route) => {
                const hidden = hiddenIds.has(route.id);
                const selected = route.id === selectedId;
                return (
                  <div
                    key={route.id}
                    ref={(node) => {
                      if (node) rowRefs.current.set(route.id, node);
                      else rowRefs.current.delete(route.id);
                    }}
                    onMouseEnter={() => setHoveredId(route.id)}
                    onMouseLeave={() => setHoveredId((current) => (current === route.id ? null : current))}
                    className={cn(
                      'group mx-1.5 mb-1 flex items-start gap-2 rounded-lg px-2 py-1.5 border transition-colors',
                      selected
                        ? 'border-brand-200 bg-brand-50'
                        : 'border-transparent hover:bg-slate-50',
                      hidden && !selected && 'opacity-50'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => selectRoute(route.id)}
                      className="flex-1 min-w-0 text-left"
                    >
                      <div className="flex items-center gap-1.5">
                        <DirectionBadge direction={route.direction} />
                        {route.routeId && (
                          <span className="font-mono text-[10.5px] text-slate-400">
                            {highlightMatch(route.routeId, search)}
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-[12.5px] font-medium text-slate-800 leading-snug line-clamp-2">
                        {highlightMatch(route.label, search)}
                      </p>
                      <p className="mt-0.5 text-[11px] text-slate-500 tabular-nums">
                        {route.totalStops} stops · {formatDistance(route.lengthMeters)}
                        {route.unlocated > 0 && (
                          <span className="text-amber-600"> · {route.unlocated} without location</span>
                        )}
                      </p>
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleHidden(route.id)}
                      title={hidden ? 'Show on map' : 'Hide from map'}
                      aria-label={hidden ? 'Show on map' : 'Hide from map'}
                      className="mt-0.5 h-6 w-6 shrink-0 inline-flex items-center justify-center rounded text-slate-400 hover:bg-white hover:text-slate-700"
                    >
                      {hidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="px-3 py-2 border-t border-slate-100 text-[11px] text-slate-500 flex items-center justify-between">
          <span className="tabular-nums">
            {filteredRoutes.length} route{filteredRoutes.length === 1 ? '' : 's'}
            {hiddenInFilter > 0 ? ` · ${hiddenInFilter} hidden` : ''}
          </span>
          <span>Lengths are straight-line</span>
        </div>
      </aside>
    </div>
  );
}

function RouteTooltip({ route }) {
  return (
    <span className="text-[12px]">
      <strong>
        Route {route.number} · {route.direction.label}
      </strong>
      <br />
      {route.label}
    </span>
  );
}

function DirectionBadge({ direction }) {
  return (
    <span
      className={cn(
        'inline-flex items-center h-[18px] px-1.5 rounded text-[10.5px] font-semibold uppercase tracking-wide',
        direction.key === 'up' && 'bg-emerald-50 text-emerald-700',
        direction.key === 'down' && 'bg-sky-50 text-sky-700',
        direction.key === 'other' && 'bg-slate-100 text-slate-600'
      )}
    >
      {direction.label}
    </span>
  );
}

function MapToolButton({ label, icon: Icon, onClick, active = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={cn(
        'h-9 w-9 inline-flex items-center justify-center rounded-lg border shadow-pop transition-colors',
        active
          ? 'bg-brand-50 border-brand-200 text-brand-700'
          : 'bg-white border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-slate-50'
      )}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

function SelectedRouteCard({ route, isolate, focusedStopKey, onToggleIsolate, onZoom, onClose, onStopClick }) {
  const first = route.stops[0];
  const last = route.stops[route.stops.length - 1];

  return (
    <div className="border-b border-slate-200 bg-slate-50/60 flex flex-col max-h-[45%] min-h-0">
      <div className="px-3 pt-3 pb-2">
        <div className="flex items-start gap-2">
          <span className="mt-1 h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: route.color }} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-[14px] font-semibold text-slate-900">Route {route.number}</p>
              <DirectionBadge direction={route.direction} />
            </div>
            {route.routeId && <p className="font-mono text-[10.5px] text-slate-400">{route.routeId}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Clear selection"
            className="h-6 w-6 inline-flex items-center justify-center rounded text-slate-400 hover:bg-white hover:text-slate-700"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {first && last && (
          <p className="mt-1.5 text-[12px] text-slate-700 flex items-center gap-1 flex-wrap">
            <span className="font-medium">{first.stopName}</span>
            <ArrowRight className="h-3 w-3 text-slate-400" />
            <span className="font-medium">{last.stopName}</span>
          </p>
        )}

        <p className="mt-1 text-[11.5px] text-slate-500 tabular-nums">
          {route.totalStops} stops · {formatDistance(route.lengthMeters)} straight-line
        </p>
        {route.unlocated > 0 && (
          <p className="mt-1 text-[11.5px] text-amber-700">
            {route.unlocated} stop{route.unlocated === 1 ? ' has' : 's have'} no location and {route.unlocated === 1 ? 'is' : 'are'} not drawn.
          </p>
        )}

        <div className="mt-2 flex items-center gap-1.5">
          <button
            type="button"
            onClick={onZoom}
            className="h-7 px-2.5 inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white text-[11.5px] font-medium text-slate-600 hover:border-brand-300 hover:text-brand-700"
          >
            <Crosshair className="h-3 w-3" />
            Zoom to route
          </button>
          <button
            type="button"
            onClick={onToggleIsolate}
            className={cn(
              'h-7 px-2.5 inline-flex items-center gap-1.5 rounded-md border text-[11.5px] font-medium',
              isolate
                ? 'border-brand-200 bg-brand-50 text-brand-700'
                : 'border-slate-200 bg-white text-slate-600 hover:border-brand-300 hover:text-brand-700'
            )}
          >
            {isolate ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
            {isolate ? 'Show others' : 'Only this route'}
          </button>
        </div>
      </div>

      <ol className="flex-1 min-h-0 overflow-y-auto px-1.5 pb-2">
        {route.stops.map((stop, index) => {
          const stopKey = `${stop.sequence}-${index}`;
          const isEnd = index === 0 || index === route.stops.length - 1;
          return (
            <li key={stopKey}>
              <button
                type="button"
                onClick={() => onStopClick(stop, stopKey)}
                className={cn(
                  'w-full flex items-center gap-2 px-2 py-1 rounded-md text-left text-[12px] transition-colors',
                  focusedStopKey === stopKey ? 'bg-white shadow-soft text-slate-900' : 'text-slate-700 hover:bg-white'
                )}
              >
                <span
                  className={cn(
                    'h-5 min-w-[20px] px-1 rounded-full text-[10px] font-semibold inline-flex items-center justify-center tabular-nums shrink-0',
                    isEnd ? 'text-white' : 'bg-white border text-slate-600'
                  )}
                  style={
                    isEnd
                      ? { backgroundColor: index === 0 ? '#059669' : '#dc2626' }
                      : { borderColor: route.color }
                  }
                >
                  {stop.sequence ?? index + 1}
                </span>
                <span className="truncate">{stop.stopName}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
